/**
 * "Land all ready": one card listing every ready branch, one passkey ceremony for all of them
 * (docs/ux/workflow-simplification.md B6). The card shows exactly what the tap approves.
 */
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@viko/transcript-ui'
import { Fingerprint, GitMerge } from 'lucide-react'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { landAll, type ReadyLanding, readyLandings } from '../lib/land-batch.js'
import { useLandGrantPicker } from '../lib/land-grant.js' // land-grant
import { qk } from '../lib/queries.js'
import type { InboxItem } from '../lib/types.js'
import { errorText, useToast } from './ui.js'

export function LandAllBar({ items }: { items: readonly InboxItem[] }) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const pick = useLandGrantPicker() // land-grant
  const ready = readyLandings(items)
  if (ready.length < 2) return null
  const grantFor = (r: ReadyLanding) =>
    pick({ workstreamId: r.item.land?.workstreamId ?? null }, r.machineId)?.proof
  const covered = ready.filter((r) => grantFor(r)).length
  const allCovered = covered === ready.length
  const run = async () => {
    setBusy(true)
    try {
      const r = await landAll({ signer: b.signer, deliver: (s) => b.deliver(s), grantFor }, ready)
      await qc.invalidateQueries({ queryKey: qk.inbox })
      if (r.failed.length)
        toast(
          `${r.landed.length} landing${r.landed.length === 1 ? '' : 's'} started; ${r.failed.length} failed: ${errorText(r.failed[0]?.error)}`,
          'error',
        )
      else toast(`${r.landed.length} landings started`)
    } catch (e) {
      if ((e as Error)?.name !== 'NotAllowedError') toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  return (
    <section
      aria-label="Land all ready branches"
      className="card mx-3 mb-4 grid gap-2 p-3 desktop:mx-4"
      data-land-all={ready.length}
    >
      <div className="flex items-start gap-2">
        <GitMerge size={15} aria-hidden="true" className="mt-0.5 flex-none text-accent-text" />
        <div className="grid min-w-0 flex-1 gap-0.5">
          <span className="text-[15px] font-semibold">{ready.length} branches ready to land</span>
          <ul className="m-0 list-none p-0 text-[14px] text-text-2">
            {ready.map((r) => (
              <li key={r.item.id} className="break-words">
                <code className="font-mono">{r.branch}</code> → main
                {r.item.projectName ? ` · ${r.item.projectName}` : ''}
              </li>
            ))}
          </ul>
        </div>
      </div>
      {covered ? (
        <p className="m-0 text-[14px] text-status-ok" data-within-grant={covered}>
          {allCovered
            ? `All ${ready.length} within landing grants: no passkey tap.`
            : `${covered} of ${ready.length} within landing grants; the other ${ready.length - covered} need one passkey tap.`}
        </p>
      ) : null}
      <Button
        variant="primary"
        className="!min-h-11 justify-self-start"
        icon={allCovered ? <GitMerge size={16} /> : <Fingerprint size={16} />}
        disabled={busy}
        onClick={() => void run()}
      >
        {busy
          ? allCovered
            ? 'Landing…'
            : 'Waiting for passkey…'
          : allCovered
            ? `Land all ${ready.length}`
            : `Land all ${ready.length} with one passkey`}
      </Button>
    </section>
  )
}
